import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  View as RNView,
  Modal as RNModal,
  useWindowDimensions,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import DropDownPicker from "react-native-dropdown-picker";
import { useSelector, useDispatch } from "react-redux";
import reportsActions from "../../../redux/actions/reportsActions";
import { useColumnResize } from "../hooks/useColumnResize";
import { invoiceDNDetailModel } from "../../../models/reportsModel";
import { getInvoiceDNDetail } from "../../../services/inventoryService";
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from "../../../utils/useTranslation";
import { scoreCardTranslationMap } from "../../../utils/translations";

const COL_ITEM_W = 220;
const COL_CUSTOMER_W = 180;
const COL_SLP_W = 140;
const FIXED_COLS_W = COL_ITEM_W + COL_CUSTOMER_W + COL_SLP_W;

const SCROLL_COLS: Array<{ key: string; label: string; width: number; numeric: boolean; expandable?: boolean }> = [
  { key: "Invoice Date", label: "Invoice Date", width: 95, numeric: false },
  { key: "Invoice Number", label: "Invoice No.", width: 80, numeric: true },
  { key: "DN Number", label: "DN No.", width: 75, numeric: true },
  { key: "DN Number Invoice Value", label: "Value ($)", width: 95, numeric: true },
  { key: "DN Number Invoice Quantity", label: "Qty", width: 70, numeric: true },
  { key: "DN Delivery Suburb", label: "Delivery Suburb", width: 160, numeric: false, expandable: true },
  { key: "Year", label: "Year", width: 50, numeric: true },
  { key: "Invoice Month", label: "Mth", width: 45, numeric: true },
  { key: "Dispatch State", label: "Dispatch St.", width: 80, numeric: false },
  { key: "Delivery State", label: "Delivery St.", width: 80, numeric: false },
  { key: "Item Group", label: "Item Group", width: 120, numeric: false },
];

const SCROLL_WIDTH = SCROLL_COLS.reduce((s, c) => s + c.width, 0);

function toItems(values: string[]) {
  return values.sort().map((v) => ({ label: v, value: v }));
}

// ── Filter dropdown wrapper ───────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  items: { label: string; value: string }[];
  open?: boolean;
  setOpen?: (o: boolean) => void;
  zIndex?: number;
  zIndexInverse?: number;
  onOpen?: () => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label, value, onChange, items,
  open, setOpen, zIndex, zIndexInverse, onOpen,
}) => {
  const { colors, isDark } = useAppTheme();
  return (
    <View style={[filterItemStyle, { zIndex }]}>
      {Platform.OS === "web" && (
        <style type="text/css">{`
        [data-hoveritem="true"]:hover, [data-hover-item="true"]:hover {
          background-color: ${isDark ? colors.surfaceSubtle : "#f5f5f5"} !important;
        }
      `}</style>
      )}
      <Text style={[filterLabelStyle, { color: colors.textSecondary }]}>{label}</Text>
      <DropDownPicker
        multiple={true}
        mode="BADGE"
        open={open!}
        setOpen={setOpen as any}
        items={items}
        value={value}
        setValue={onChange as any}
        placeholder="All"
        listMode="SCROLLVIEW"
        maxHeight={600}
        theme={isDark ? "DARK" : "LIGHT"}
        style={{ borderColor: colors.border, borderRadius: 6, minHeight: 38, backgroundColor: colors.surface }}
        dropDownContainerStyle={{ borderColor: colors.border, borderRadius: 6, backgroundColor: colors.surface }}
        zIndex={zIndex}
        zIndexInverse={zIndexInverse}
        onOpen={onOpen}
        badgeDotColors={[colors.primary]}
        badgeColors={isDark ? ["#334155"] : ["#E2E8F0"]}
        badgeTextStyle={{ color: colors.text }}
        searchable={true}
        searchPlaceholder="Search..."
        searchContainerStyle={{ borderBottomColor: colors.borderSubtle, borderBottomWidth: 1, padding: 10 }}
        searchTextInputStyle={{ borderColor: colors.border, borderWidth: 1, borderRadius: 6, height: 36, paddingHorizontal: 12, fontSize: 13, color: colors.text, backgroundColor: colors.surface }}
        itemProps={{ dataSet: { hoveritem: "true" } } as any}
        dropDownDirection="BOTTOM"
        textStyle={{ color: colors.text }}
      />
    </View>
  )
};

const filterItemStyle = { minWidth: 160, flex: 1 } as const;
const filterLabelStyle = { fontSize: 12, color: "#555", marginBottom: 4, fontWeight: "500" as const };

function getCacheWindowStart(): string {
  const now = new Date();
  const start = new Date(now);
  start.setHours(now.getHours() < 12 ? 0 : 12, 0, 0, 0);
  return start.toLocaleString("en-AU", { hour12: true });
}

function formatScrollCell(col: { key: string; numeric: boolean }, raw: any): string {
  if (col.key === "DN Number") return raw ? String(raw) : "-";
  if (col.key === "DN Number Invoice Value") {
    return (raw as number || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (col.key === "DN Number Invoice Quantity") return (raw as number || 0).toLocaleString();
  return raw != null ? String(raw) : "";
}

const rszStyle = { position: "absolute" as const, right: 0, top: 0, bottom: 0, width: 6, cursor: "col-resize", zIndex: 2 };

// ── Main screen ───────────────────────────────────────────────────────────────
export default function InvoiceDNDetailScreen() {
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { invoiceDNDetailReport, isInvoiceDNDetailReportListLoaded, invoiceDNDetailHiddenColumns } = useSelector(
    (state: any) => state.reports
  );
  const rows: invoiceDNDetailModel[] = invoiceDNDetailReport;
  const hiddenCols = invoiceDNDetailHiddenColumns || [];
  const searchScrollRef = useRef<any>(null);

  useEffect(() => {
    getInvoiceDNDetail();
  }, []);

  // ── Horizontal scroll sync ────────────────────────────────────────────────
  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const onBodyHScroll = useRef(
    Animated.event(
      [{ nativeEvent: { contentOffset: { x: scrollAnim } } }],
      { useNativeDriver: false }
    )
  ).current;

  // ── Data area height ──────────────────────────────────────────────────────
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [dataAreaHeight, setDataAreaHeight] = useState(() => Math.max(200, windowHeight - 380));
  const onDataAreaLayout = useCallback((e: any) => {
    setDataAreaHeight(e.nativeEvent.layout.height);
  }, []);

  const defaultColWidths = useMemo(() => {
    const visibleFixedW = (hiddenCols.includes("Item Name") ? 0 : COL_ITEM_W) +
      (hiddenCols.includes("Customer") ? 0 : COL_CUSTOMER_W) +
      (hiddenCols.includes("Sales Person") ? 0 : COL_SLP_W);
    const availableForScroll = windowWidth - 48 - visibleFixedW;
    const visibleScrollCols = SCROLL_COLS.filter(c => !hiddenCols.includes(c.key));
    const defaultScrollTotal = visibleScrollCols.reduce((s, c) => s + c.width, 0);
    const scale = availableForScroll > defaultScrollTotal && defaultScrollTotal > 0 ? availableForScroll / defaultScrollTotal : 1;
    const obj: Record<string, number> = { "Item Name": COL_ITEM_W, Customer: COL_CUSTOMER_W, "Sales Person": COL_SLP_W };
    for (const c of SCROLL_COLS) obj[c.key] = Math.floor(c.width * scale);
    return obj;
  }, [windowWidth, hiddenCols]);

  const [colWidths, setColWidths] = useState<Record<string, number>>(defaultColWidths);
  useEffect(() => {
    setColWidths((prev) => {
      const obj: Record<string, number> = { ...prev };
      for (const c of SCROLL_COLS) obj[c.key] = defaultColWidths[c.key];
      return obj;
    });
  }, [defaultColWidths]);
  const startResize = useColumnResize(setColWidths);
  const tableWidth = Object.entries(colWidths).reduce((s, [k, w]) => hiddenCols.includes(k) ? s : s + w, 0);

  // ── Copy to clipboard ─────────────────────────────────────────────────────
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyCell = useCallback((key: string, text: string) => {
    if (Platform.OS === "web" && typeof navigator !== "undefined") {
      try { navigator.clipboard?.writeText(text); } catch { }
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1200);
  }, []);

  const CellWrapper: any = Platform.OS === "web" ? View : TouchableOpacity;

  // ── Filter open state (native only) ──────────────────────────────────────
  const [yearOpen, setYearOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [slpOpen, setSlpOpen] = useState(false);
  const [itemGroupOpen, setItemGroupOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  const allColOptions = useMemo(() => [
    { label: t(scoreCardTranslationMap["Item Name"] || "itemName") || "Item Name", value: "Item Name" },
    { label: t(scoreCardTranslationMap["Customer"] || "customerName") || "Customer", value: "Customer" },
    { label: t(scoreCardTranslationMap["Sales Person"] || "salesPerson") || "Sales Person", value: "Sales Person" },
    ...SCROLL_COLS.map(c => ({ label: t(scoreCardTranslationMap[c.label] || c.label) || c.label, value: c.key }))
  ], [t]);

  const columnOrder = useSelector((state: any) => state.reports.invoiceDNDetailColumnOrder) || [];

  const getOrderedKeys = useCallback((allKeys: string[]) => {
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k: string) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k: string) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  }, [columnOrder]);

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allKeys = allColOptions.map((c) => c.value);
    const currentKeys = getOrderedKeys(allKeys).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    dispatch(reportsActions.setInvoiceDNDetailColumnOrder(newKeys));
    AsyncStorage.setItem("invoice_scorecard_column_order", JSON.stringify(newKeys));
  }, [columnOrder, allColOptions, getOrderedKeys, dispatch]);

  const resetColumnOrder = () => {
    dispatch(reportsActions.setInvoiceDNDetailColumnOrder([]));
    dispatch(reportsActions.setInvoiceDNDetailHiddenColumns([]));
    AsyncStorage.removeItem("invoice_scorecard_column_order");

    setColWidths(defaultColWidths);
  };

  useEffect(() => {
    if (columnOrder.length === 0) {
      AsyncStorage.getItem("invoice_scorecard_column_order").then((val) => {
        if (val) {
          try {
            const parsed = JSON.parse(val);
            if (parsed && parsed.length > 0) {
              dispatch(reportsActions.setInvoiceDNDetailColumnOrder(parsed));
            }
          } catch { }
        }
      });
    }
  }, []);

  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);



  const toggleColumn = (columnKey: string) => {
    const updated = hiddenCols.includes(columnKey)
      ? hiddenCols.filter((k: string) => k !== columnKey)
      : [...hiddenCols, columnKey];
    dispatch(reportsActions.setInvoiceDNDetailHiddenColumns(updated));
  };

  // ── Filter values (empty array = show all) ────────────────────────────────
  const [yearVal, setYearVal] = useState<string[]>([]);
  const [dispatchVal, setDispatchVal] = useState<string[]>([]);
  const [deliveryVal, setDeliveryVal] = useState<string[]>([]);
  const [slpVal, setSlpVal] = useState<string[]>([]);
  const [itemGroupVal, setItemGroupVal] = useState<string[]>([]);
  const [customerVal, setCustomerVal] = useState<string[]>([]);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");

  const availableYears = useMemo(
    () => Array.from(new Set(rows.map((r) => String(r.Year)).filter(Boolean))).sort().reverse(),
    [rows]
  );

  const yearItems = useMemo(() => toItems(availableYears), [availableYears]);
  const dispatchItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Dispatch State"]).filter(Boolean)))), [rows]);
  const deliveryItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Delivery State"]).filter(Boolean)))), [rows]);
  const slpItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Sales Person"]).filter(Boolean)))), [rows]);
  const itemGroupItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Item Group"]).filter(Boolean)))), [rows]);
  const customerItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.Customer).filter(Boolean)))), [rows]);

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const toggleRow = (key: number) => setExpandedRow((prev) => (prev === key ? null : key));

  const onSortPress = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setCurrentPage(1);
    setExpandedRow(null);
  };

  const sortIndicator = (col: string) =>
    sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const onSearchChange = (text: string) => {
    if (text.includes(",")) {
      const before = text.slice(0, text.indexOf(",")).trim();
      if (before) setSearchTerms((prev) => [...prev, before]);
      setSearchInput("");
    } else {
      setSearchInput(text);
    }
  };

  const filtered = useMemo(() => {
    const terms = [...searchTerms, searchInput.trim()].map((s) => s.toLowerCase()).filter(Boolean);
    return rows.filter((r) => {
      if (yearVal.length > 0 && !yearVal.includes(String(r.Year))) return false;
      if (dispatchVal.length > 0 && !dispatchVal.includes(r["Dispatch State"])) return false;
      if (deliveryVal.length > 0 && !deliveryVal.includes(r["Delivery State"])) return false;
      if (slpVal.length > 0 && !slpVal.includes(r["Sales Person"])) return false;
      if (itemGroupVal.length > 0 && !itemGroupVal.includes(r["Item Group"])) return false;
      if (customerVal.length > 0 && !customerVal.includes(r.Customer)) return false;
      if (terms.length > 0) {
        const text = `${r["Item Name"] ?? ""} ${r.Customer ?? ""} ${r["Sales Person"] ?? ""} ${r["Invoice Date"] ?? ""} ${r["DN Number"] ?? ""} ${r["DN Delivery Suburb"] ?? ""}`.toLowerCase();
        if (!terms.every((t) => text.includes(t))) return false;
      }
      return true;
    });
  }, [rows, yearVal, dispatchVal, deliveryVal, slpVal, itemGroupVal, customerVal, searchTerms, searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [yearVal, dispatchVal, deliveryVal, slpVal, itemGroupVal, customerVal, searchTerms, searchInput]);

  const colTotals = useMemo(() => ({
    "DN Number Invoice Value": filtered.reduce((s, r) => s + (r["DN Number Invoice Value"] || 0), 0),
    "DN Number Invoice Quantity": filtered.reduce((s, r) => s + (r["DN Number Invoice Quantity"] || 0), 0),
  }), [filtered]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortCol as keyof invoiceDNDetailModel];
      const bVal = b[sortCol as keyof invoiceDNDetailModel];

      if (sortCol === "Invoice Date") {
        const parseDate = (d: any) => {
          if (!d) return 0;
          const str = String(d);
          const match = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
          if (match) {
            return new Date(`${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}T00:00:00Z`).getTime();
          }
          return new Date(str).getTime() || 0;
        };
        const dA = parseDate(aVal);
        const dB = parseDate(bVal);
        return sortDir === "asc" ? dA - dB : dB - dA;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();
      return sortDir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filtered, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goToPage = (p: number) => { setCurrentPage(Math.max(1, Math.min(p, totalPages))); setExpandedRow(null); };
  const onPageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); setExpandedRow(null); };

  const colItem = [styles.colBase, { width: colWidths["Item Name"] }, hiddenCols.includes("Item Name") && { display: "none" as const }];
  const colCustomer = [styles.colBase, { width: colWidths.Customer }, hiddenCols.includes("Customer") && { display: "none" as const }];
  const colSlp = [styles.colBase, { width: colWidths["Sales Person"] }, hiddenCols.includes("Sales Person") && { display: "none" as const }];

  if (!isInvoiceDNDetailReportListLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading Invoice/DN Details...</Text>
      </View>
    );
  }

  const anyOpen = yearOpen || dispatchOpen || deliveryOpen || slpOpen || itemGroupOpen || customerOpen;
  const closeAllDropdowns = () => {
    setYearOpen(false);
    setDispatchOpen(false);
    setDeliveryOpen(false);
    setSlpOpen(false);
    setItemGroupOpen(false);
    setCustomerOpen(false);
  };

  const resetFilters = () => {
    setYearVal([]);
    setDispatchVal([]);
    setDeliveryVal([]);
    setSlpVal([]);
    setItemGroupVal([]);
    setCustomerVal([]);
    setSearchTerms([]);
    setSearchInput("");
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {anyOpen && (
        <Pressable
          style={{ position: Platform.OS === "web" ? ("fixed" as any) : "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, height: Platform.OS === "web" ? ("100vh" as any) : 2000 }}
          onPress={closeAllDropdowns}
        />
      )}
      {/* ── Frozen header: title + search + filters ────────────────────────── */}
      <View style={[styles.frozenHeader, { backgroundColor: colors.surface }]}>
        <View style={styles.filterPanel}>
          <View style={styles.filterHeaderRow}>
            <Text style={styles.filterHeading}>{t(scoreCardTranslationMap["FILTERS"] || "filters") || "Filters"}</Text>
            <TouchableOpacity onPress={resetFilters} style={styles.resetBtn}>
              <Text style={styles.resetBtnText}>{t("resetFilter") || "Reset Filter"}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.filterGrid}>
            <FilterDropdown
              label={t("year") || "Year"}
              value={yearVal} onChange={setYearVal} items={yearItems}
              open={yearOpen} setOpen={setYearOpen} zIndex={700} zIndexInverse={100}
              onOpen={() => { setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label={t("salesPerson") || "Sales Person"}
              value={slpVal} onChange={setSlpVal} items={slpItems}
              open={slpOpen} setOpen={setSlpOpen} zIndex={600} zIndexInverse={200}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label={t("dispatchState") || "Dispatch State"}
              value={dispatchVal} onChange={setDispatchVal} items={dispatchItems}
              open={dispatchOpen} setOpen={setDispatchOpen} zIndex={500} zIndexInverse={300}
              onOpen={() => { setYearOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label={t("deliveryState") || "Delivery State"}
              value={deliveryVal} onChange={setDeliveryVal} items={deliveryItems}
              open={deliveryOpen} setOpen={setDeliveryOpen} zIndex={400} zIndexInverse={400}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label={t("itemGroup") || "Item Group"}
              value={itemGroupVal} onChange={setItemGroupVal} items={itemGroupItems}
              open={itemGroupOpen} setOpen={setItemGroupOpen} zIndex={300} zIndexInverse={500}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label={t("customer") || "Customer"}
              value={customerVal} onChange={setCustomerVal} items={customerItems}
              open={customerOpen} setOpen={setCustomerOpen} zIndex={200} zIndexInverse={600}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); }}
            />
          </View>
        </View>
      </View>

      {/* ── Table section ──────────────────────────────────────────────────── */}
      <View style={styles.tableSection}>
        <View style={[styles.tableCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>

          {/* Top bar */}
          <View style={styles.tableTopBar}>
            <View style={{ flexDirection: "row", alignItems: "baseline" }}>
              <Text style={styles.tableTitle}>
                {t("invoiceLines")} — {filtered.length.toLocaleString()} {t("rows")}
              </Text>
              <Text style={[styles.fetchedAt, { marginBottom: 0, marginLeft: 12 }]}>
                {t("dataUpTo")} {getCacheWindowStart()}
              </Text>
            </View>
            <View style={styles.pageSizeRow}>
              {(sortCol !== null || JSON.stringify(colWidths) !== JSON.stringify(defaultColWidths)) && (
                <TouchableOpacity
                  onPress={() => {
                    setSortCol(null);
                    setColWidths(defaultColWidths);
                  }}
                  style={styles.resetSortBtn}
                >
                  <Text style={styles.resetSortTxt}>{t("reset") || "Reset"}</Text>
                </TouchableOpacity>
              )}
              <RNView style={{ marginRight: 12 }}>
                <TouchableOpacity
                  ref={settingsBtnRef}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    backgroundColor: columnsOpen ? (isDark ? "#2a3b4c" : "#E0E7FF") : (isDark ? "#333" : "#F3F4F6"),
                    justifyContent: "center",
                    alignItems: "center",
                    borderWidth: 1,
                    borderColor: columnsOpen ? (isDark ? "#56B7FD" : "#818CF8") : colors.border,
                  }}
                  onPress={() => {
                    if (settingsBtnRef.current) {
                      settingsBtnRef.current.measureInWindow(
                        (x: number, y: number, width: number, height: number) => {
                          setSettingsBtnPos({ x, y, width, height });
                          setColumnsOpen(!columnsOpen);
                        }
                      );
                    } else {
                      setColumnsOpen(!columnsOpen);
                    }
                  }}
                >
                  <Feather
                    name="settings"
                    size={14}
                    color={columnsOpen ? "#56B7FD" : (isDark ? "#aaa" : "#6B7280")}
                  />
                </TouchableOpacity>
                {columnsOpen && (
                  <RNModal
                    transparent={true}
                    visible={columnsOpen}
                    animationType="fade"
                    onRequestClose={() => setColumnsOpen(false)}
                  >
                    <Pressable
                      style={{
                        flex: 1,
                        backgroundColor: "rgba(0,0,0,0.05)",
                      }}
                      onPress={() => setColumnsOpen(false)}
                    >
                      <Pressable
                        style={{
                          position: "absolute",
                          top: settingsBtnPos.y + settingsBtnPos.height + 6,
                          left: settingsBtnPos.x - 188, // shift left so it doesn't clip screen
                          backgroundColor: colors.surface,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: colors.border,
                          paddingVertical: 10,
                          minWidth: 220,
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 8 },
                          shadowOpacity: 0.15,
                          shadowRadius: 20,
                          elevation: 10,
                        }}
                        onPress={(e) => e.stopPropagation()}
                      >
                        <Text
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: colors.textSecondary,
                            paddingHorizontal: 16,
                            paddingBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {t("showHideColumns") || "Show / Hide Columns"}
                        </Text>
                        <RNView style={{ height: 1, backgroundColor: colors.borderSubtle, marginBottom: 4 }} />
                        <TouchableOpacity
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingVertical: 8,
                            paddingHorizontal: 16,
                          }}
                          onPress={() => {
                            if (hiddenCols.length === 0) {
                              dispatch(reportsActions.setInvoiceDNDetailHiddenColumns(allColOptions.map((c) => c.value)));
                            } else {
                              dispatch(reportsActions.setInvoiceDNDetailHiddenColumns([]));
                            }
                          }}
                        >
                          <RNView
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              borderWidth: 1.5,
                              borderColor: hiddenCols.length === 0 ? colors.primary : colors.textMuted,
                              backgroundColor: hiddenCols.length === 0 ? colors.primary : colors.surface,
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: 10,
                            }}
                          >
                            {hiddenCols.length === 0 && (
                              <Feather name="check" size={12} color="#fff" />
                            )}
                          </RNView>
                          <Text
                            style={{
                              fontSize: 13,
                              color: colors.text,
                              fontWeight: "700",
                            }}
                          >
                            {t("selectAll") || "Select All"}
                          </Text>
                        </TouchableOpacity>
                        <RNView style={{ height: 1, backgroundColor: colors.borderSubtle, marginBottom: 2 }} />
                        <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                          {getOrderedKeys(allColOptions.map(c => c.value)).map((colValue) => {
                            const col = allColOptions.find(c => c.value === colValue);
                            if (!col) return null;
                            const isVisible = !hiddenCols.includes(col.value);
                            const isDragging = dragActiveKey === col.value;
                            const isDragOver = dragOverKey === col.value;
                            return (
                              <RNView
                                key={col.value}
                                {...{ dataSet: { columnkey: col.value } } as any}
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  paddingVertical: 8,
                                  paddingHorizontal: 16,
                                  opacity: isDragging ? 0.35 : 1,
                                  backgroundColor: isDragOver ? (isDark ? colors.surfaceSubtle : "#EFF6FF") : "transparent",
                                  borderTopWidth: isDragOver ? 2 : 0,
                                  borderTopColor: colors.primary,
                                }}
                              >
                                <Pressable
                                  onPressIn={() => startColumnDrag(col.value)}
                                  style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                >
                                  <Feather name="menu" size={12} color={colors.textMuted} />
                                </Pressable>
                                <TouchableOpacity
                                  style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                  onPress={() => toggleColumn(col.value)}
                                >
                                  <RNView
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: 4,
                                      borderWidth: 1.5,
                                      borderColor: isVisible ? colors.primary : colors.textMuted,
                                      backgroundColor: isVisible ? colors.primary : colors.surface,
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 10,
                                    }}
                                  >
                                    {isVisible && (
                                      <Feather name="check" size={12} color="#fff" />
                                    )}
                                  </RNView>
                                  <Text style={{ fontSize: 13, color: isVisible ? colors.text : colors.textMuted, fontWeight: isVisible ? "500" : "400" }}>
                                    {col.label}
                                  </Text>
                                </TouchableOpacity>
                              </RNView>
                            );
                          })}
                        </ScrollView>
                        {(columnOrder.length > 0 || hiddenCols.length > 0) && (
                          <>
                            <RNView style={{ height: 1, backgroundColor: colors.borderSubtle, marginTop: 4 }} />
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 10,
                                paddingHorizontal: 16,
                              }}
                              onPress={resetColumnOrder}
                            >
                              <Feather name="rotate-ccw" size={13} color="#6B7280" />
                              <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                {t("resetLayout") || "Reset Layout"}
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </Pressable>
                    </Pressable>
                  </RNModal>
                )}
              </RNView>
              <View style={styles.searchContainer}>
                <ScrollView ref={searchScrollRef} onContentSizeChange={() => searchScrollRef.current?.scrollToEnd({ animated: true })} horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }} contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}>
                  {searchTerms.map((term, i) => (
                    <View key={i} style={styles.searchChip}>
                      <Text style={styles.searchChipText}>{term}</Text>
                      <TouchableOpacity style={styles.searchChipRemove} onPress={() => setSearchTerms((prev) => prev.filter((_, j) => j !== i))}>
                        <Text style={styles.searchChipX}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                  <TextInput
                    style={styles.searchInputInner}
                    value={searchInput}
                    onChangeText={onSearchChange}
                    placeholder={searchTerms.length === 0 ? (t("searchPlaceholder") || "Search item, customer, sales person...") : (t("addCriteria") || "Add criteria...")}
                    placeholderTextColor="#aaa"
                    onFocus={closeAllDropdowns}
                  />
                </ScrollView>
              </View>
              <Text style={styles.pageSizeLabel}>{t("rowsPerPage") || "Rows per page:"}</Text>
              {[50, 100, 200].map((n) => (
                <TouchableOpacity
                  key={n}
                  onPress={() => onPageSizeChange(n)}
                  style={[styles.pageSizeBtn, pageSize === n && styles.pageSizeBtnActive]}
                >
                  <Text style={[styles.pageSizeTxt, pageSize === n && styles.pageSizeTxtActive]}>
                    {n}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* ── Frozen column header ──────────────────────────────────────── */}
          <View style={{ overflow: "hidden" }}>
            <Animated.View
              style={[styles.tableHeader, { width: tableWidth, transform: [{ translateX: negScrollAnim }] }]}
            >
              {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                if (colKey === "Item Name") return (
                  <View key={colKey} style={colItem as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Item Name")}>
                      <Text style={styles.headerCell}>{t(scoreCardTranslationMap["Item Name"] || "itemName") || "Item Name"}{sortIndicator("Item Name")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Item Name", colWidths["Item Name"]) } as any} />}
                  </View>
                );
                if (colKey === "Customer") return (
                  <View key={colKey} style={colCustomer as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Customer")}>
                      <Text style={styles.headerCell}>{t(scoreCardTranslationMap["Customer"] || "customerName") || "Customer"}{sortIndicator("Customer")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Customer", colWidths.Customer) } as any} />}
                  </View>
                );
                if (colKey === "Sales Person") return (
                  <View key={colKey} style={colSlp as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Sales Person")}>
                      <Text style={styles.headerCell}>{t(scoreCardTranslationMap["Sales Person"] || "salesPerson") || "Sales Person"}{sortIndicator("Sales Person")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Sales Person", colWidths["Sales Person"]) } as any} />}
                  </View>
                );
                const col = SCROLL_COLS.find(c => c.key === colKey);
                if (!col) return null;
                return (
                  <View key={col.key} style={[{ paddingHorizontal: 4, paddingVertical: 6, width: colWidths[col.key], borderRightWidth: 1, borderRightColor: isDark ? "#444" : "#dde3ea" }, hiddenCols.includes(col.key) && { display: "none" as const }]}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress(col.key)}>
                      <Text style={[styles.headerCell, col.numeric && styles.rightAlign]}>
                        {t(scoreCardTranslationMap[col.label] || col.label) || col.label}{sortIndicator(col.key)}
                      </Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, col.key, colWidths[col.key]) } as any} />}
                  </View>
                );
              })}
            </Animated.View>
          </View>

          {/* ── Scrollable data rows ──────────────────────────────────────── */}
          <View style={{ flex: 1 }} onLayout={onDataAreaLayout}>
            <ScrollView
              horizontal
              onScroll={onBodyHScroll}
              scrollEventThrottle={16}
              showsHorizontalScrollIndicator={false}
            >
              <ScrollView
                style={{ height: dataAreaHeight }}
                nestedScrollEnabled
                showsVerticalScrollIndicator
              >
                <View style={{ width: tableWidth }}>
                  {pageRows.map((row, idx) => {
                    const rowKey = (safePage - 1) * pageSize + idx;
                    const expanded = expandedRow === rowKey;
                    return (
                      <View key={rowKey} style={[styles.tableRow, idx % 2 === 1 && styles.altRow]}>

                        {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                          if (colKey === "Item Name") return (
                            <CellWrapper key={colKey} style={[colItem as any, copiedKey === `${rowKey}-item` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-item`, row["Item Name"])}>
                              <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                {row["Item Name"]}
                              </Text>
                            </CellWrapper>
                          );
                          if (colKey === "Customer") return (
                            <CellWrapper key={colKey} style={[colCustomer as any, copiedKey === `${rowKey}-cust` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-cust`, row.Customer)}>
                              <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                {row.Customer}
                              </Text>
                            </CellWrapper>
                          );
                          if (colKey === "Sales Person") return (
                            <CellWrapper key={colKey} style={[colSlp as any, copiedKey === `${rowKey}-slp` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-slp`, row["Sales Person"])}>
                              <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                {row["Sales Person"]}
                              </Text>
                            </CellWrapper>
                          );
                          const col = SCROLL_COLS.find(c => c.key === colKey);
                          if (!col) return null;
                          const raw = row[col.key as keyof invoiceDNDetailModel];
                          const display = formatScrollCell(col, raw);
                          const ck = `${rowKey}-${col.key}`;
                          return (
                            <CellWrapper key={col.key} style={[{ paddingHorizontal: 4, paddingVertical: 6, width: colWidths[col.key], borderRightWidth: 1, borderRightColor: colors.borderSubtle }, copiedKey === ck ? styles.copiedCell : undefined, hiddenCols.includes(col.key) && { display: "none" as const }]} onLongPress={col.expandable ? () => copyCell(ck, display) : () => copyCell(ck, display)}>
                              <Text selectable numberOfLines={col.expandable && expanded ? undefined : 1} style={[styles.cell, col.numeric && styles.rightAlign, col.expandable && styles.expandableCell]} onPress={col.expandable ? () => toggleRow(rowKey) : undefined}>
                                {display}
                              </Text>
                            </CellWrapper>
                          );
                        })}

                      </View>
                    );
                  })}
                </View>
              </ScrollView>
            </ScrollView>
          </View>

          {/* ── Frozen totals row ─────────────────────────────────────────── */}
          <View style={{ overflow: "hidden" }}>
            <Animated.View
              style={[styles.totalsRow, { width: tableWidth, transform: [{ translateX: negScrollAnim }] }]}
            >
              {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                if (colKey === "Item Name") return (
                  <View key={colKey} style={colItem}>
                    <Text style={styles.totalCell}>{t("total") || "TOTAL"}</Text>
                  </View>
                );
                if (colKey === "Customer") return (
                  <View key={colKey} style={colCustomer}>
                    <Text style={[styles.totalCell, styles.totalSubLabel]}>
                      {filtered.length.toLocaleString()} {t("filteredRows") || "filtered rows"}
                    </Text>
                  </View>
                );
                if (colKey === "Sales Person") return (
                  <View key={colKey} style={colSlp} />
                );
                const col = SCROLL_COLS.find(c => c.key === colKey);
                if (!col) return null;
                let display = "";
                if (col.key === "DN Number Invoice Value") {
                  display = colTotals["DN Number Invoice Value"].toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                } else if (col.key === "DN Number Invoice Quantity") {
                  display = colTotals["DN Number Invoice Quantity"].toLocaleString();
                }
                const ck = `ttl-${col.key}`;
                return (
                  <CellWrapper
                    key={col.key}
                    style={[{ paddingHorizontal: 4, paddingVertical: 6, width: colWidths[col.key], borderRightWidth: 1, borderRightColor: isDark ? "#1565c0" : "#90caf9" }, copiedKey === ck ? styles.copiedCell : undefined, hiddenCols.includes(col.key) && { display: "none" as const }]}
                    onLongPress={() => display ? copyCell(ck, display) : undefined}
                  >
                    <Text selectable style={[styles.totalCell, col.numeric && styles.rightAlign]}>
                      {display}
                    </Text>
                  </CellWrapper>
                );
              })}
            </Animated.View>
          </View>

          {/* Pagination */}
          <View style={styles.paginationRow}>
            <Text style={styles.paginationInfo}>
              {filtered.length === 0
                ? "No results"
                : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length.toLocaleString()}`}
            </Text>
            <View style={styles.paginationBtns}>
              <TouchableOpacity onPress={() => goToPage(1)} disabled={safePage === 1} style={[styles.pageBtn, safePage === 1 && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>«</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => goToPage(safePage - 1)} disabled={safePage === 1} style={[styles.pageBtn, safePage === 1 && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>‹</Text>
              </TouchableOpacity>
              <Text style={styles.pageIndicator}>{t("page") || "Page"} {safePage} / {totalPages}</Text>
              <TouchableOpacity onPress={() => goToPage(safePage + 1)} disabled={safePage === totalPages} style={[styles.pageBtn, safePage === totalPages && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>›</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => goToPage(totalPages)} disabled={safePage === totalPages} style={[styles.pageBtn, safePage === totalPages && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>»</Text>
              </TouchableOpacity>
            </View>
          </View>

        </View>
      </View>
    </View>
  );
}

function createStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: isDark ? colors.background : "#f5f5f5" },
    loadingContainer: { flex: 1, justifyContent: "center", alignItems: "center" },
    loadingText: { marginTop: 12, fontSize: 14, color: isDark ? "#aaa" : "#555" },

    frozenHeader: { backgroundColor: isDark ? colors.background : "#f5f5f5", padding: 16, paddingBottom: 0, zIndex: 1000 },
    pageTitle: { fontSize: 14, fontWeight: "600", color: isDark ? "#ccc" : "#333", marginBottom: 2 },
    fetchedAt: { fontSize: 11, fontStyle: "italic", color: isDark ? "#888" : "#888", marginBottom: 6 },
    searchContainer: {
      flexDirection: "row", flexWrap: "nowrap", alignItems: "center",
      borderWidth: 1, borderColor: isDark ? "#444" : "#D1D5DB", borderRadius: 8,
      paddingHorizontal: 8, paddingVertical: 4,
      backgroundColor: colors.surface, minHeight: 36,
      width: 320, marginRight: 16,
    },
    searchChip: {
      flexDirection: "row", alignItems: "center",
      backgroundColor: isDark ? colors.primary + "33" : "#DBEAFE", borderRadius: 20,
      paddingLeft: 10, paddingRight: 6, paddingVertical: 4,
      marginRight: 4, marginVertical: 2,
    },
    searchChipText: { fontSize: 12, color: isDark ? "#93C5FD" : "#1D4ED8", fontWeight: "600" },
    searchChipRemove: {
      marginLeft: 6, width: 16, height: 16, borderRadius: 8,
      backgroundColor: isDark ? colors.primary + "66" : "#93C5FD", justifyContent: "center", alignItems: "center",
    },
    searchChipX: { fontSize: 9, color: isDark ? "#fff" : "#1E40AF", fontWeight: "700", lineHeight: 16 },
    searchInputInner: { flex: 1, minWidth: 250, fontSize: 13, height: 24, padding: 0, color: colors.text },
    filterPanel: {
      backgroundColor: colors.surface, borderRadius: 8, padding: 14, marginBottom: 16,
      shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08, shadowRadius: 3, elevation: 2,
      zIndex: 1000,
    },
    filterHeaderRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
    filterHeading: {
      fontSize: 12, fontWeight: "600", color: isDark ? "#888" : "#888",
      textTransform: "uppercase", letterSpacing: 0.5,
    },
    resetBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4, backgroundColor: isDark ? "#333" : "#f0f0f0" },
    resetBtnText: { fontSize: 11, color: isDark ? "#aaa" : "#555", fontWeight: "600" as const },
    filterGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12, zIndex: 1000 },

    tableSection: { flex: 1, padding: 8, paddingTop: 0, paddingBottom: 8 },
    tableCard: {
      flex: 1, backgroundColor: colors.surface, borderRadius: 8, padding: 16,
      shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08, shadowRadius: 3, elevation: 2,
    },

    tableTopBar: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 },
    tableTitle: { fontSize: 15, fontWeight: "600", color: isDark ? "#ccc" : "#333" },
    pageSizeRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    pageSizeLabel: { fontSize: 12, color: isDark ? "#aaa" : "#555" },
    pageSizeBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, borderWidth: 1, borderColor: isDark ? "#444" : "#ddd", backgroundColor: colors.surface },
    pageSizeBtnActive: { backgroundColor: "#1976d2", borderColor: "#1976d2" },
    pageSizeTxt: { fontSize: 12, color: isDark ? "#ccc" : "#333" },
    pageSizeTxtActive: { color: "#fff", fontWeight: "600" },
    resetSortBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, backgroundColor: "#fff0f0", borderWidth: 1, borderColor: "#ffcdd2", marginRight: 8 },
    resetSortTxt: { fontSize: 12, color: "#d32f2f", fontWeight: "600" },

    tableHeader: { flexDirection: "row", backgroundColor: isDark ? colors.surfaceVariant || "#2c2c2e" : "#f0f4f8", borderTopLeftRadius: 4, borderTopRightRadius: 4, borderBottomWidth: 2, borderBottomColor: isDark ? "#444" : "#dde3ea" },
    tableRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: isDark ? "#333" : "#f0f0f0" },
    altRow: { backgroundColor: isDark ? colors.surface : "#fafafa" },
    totalsRow: { flexDirection: "row", backgroundColor: isDark ? "#1A2E44" : "#e3f2fd", borderTopWidth: 2, borderTopColor: "#1976d2" },

    headerCell: { fontWeight: "700", color: isDark ? "#bbb" : "#444", fontSize: 11 },
    cell: { fontSize: 11, color: isDark ? "#ccc" : "#333" },
    expandableCell: { color: "#1976d2" },
    rightAlign: { textAlign: "right" },
    totalCell: { fontSize: 11, fontWeight: "700", color: isDark ? "#90CAF9" : "#0d47a1" },
    totalSubLabel: { fontSize: 10, fontWeight: "400", color: isDark ? "#888" : "#888" },

    copiedCell: { backgroundColor: isDark ? "#1b5e20" : "#c8e6c9" },

    colBase: { paddingHorizontal: 8, paddingVertical: 6, borderRightWidth: 1, borderRightColor: isDark ? "#333" : "#eee" },

    paginationRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap", gap: 8 },
    paginationInfo: { fontSize: 12, color: isDark ? "#aaa" : "#666" },
    paginationBtns: { flexDirection: "row", alignItems: "center", gap: 4 },
    pageBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, borderWidth: 1, borderColor: isDark ? "#444" : "#ddd", backgroundColor: colors.surface },
    pageBtnDisabled: { borderColor: "#eee", backgroundColor: isDark ? colors.background : "#f5f5f5" },
    pageBtnTxt: { fontSize: 14, color: isDark ? "#ccc" : "#333" },
    pageIndicator: { fontSize: 12, color: isDark ? "#aaa" : "#555", marginHorizontal: 8 },
  });
}
